import { createStyles, Grid, makeStyles, Theme, useMediaQuery } from '@material-ui/core';
import Client from '@spike/client-model';
import { Address, FieldError } from '@spike/model';
import useNonInitialEffect from '@versiondos/hooks';
import { ControlledTextField2 } from 'components/UI';
import ControlledDescriptionField2 from 'components/UI/ControlledDescriptionField2';
import ControlledZipCodeField3 from 'components/UI/ControlledZipCodeField3';
import ControlledPhoneField3 from 'components/UI/ControlledPhoneField3';
import { useMarketplace, usePermissions } from 'hooks';
import set from 'lodash/set';
import { FunctionComponent } from 'react';

interface PersonalInformationProps {
    client: Client;
    errors: Array<FieldError>;
    onChange?: (client: Client) => void;
    className?: string;
    disabled?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        inputLabel: {
            fontSize: 14,
            fontWeight: 400
        },
        lineDivider: {
            width: 'inherit',
            marginTop: '40px',
            marginBottom: '23px',
            marginRight: '18px',
            marginLeft: '18px',
            borderColor: '#D4D4D4'
        }
    })
);

export const PersonalInformation: FunctionComponent<PersonalInformationProps> = props => {
    const marketplace = useMarketplace();

    const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const permissions = usePermissions();
    const isSensitiveContactEnabled = permissions.clients.viewSensitiveContact;

    const classes = useStyles();

    const normalizeAddress = (address: any) => {
        return {
            ...address,
            street: address?.street || '',
            city: address?.city || '',
            state: address?.state || '',
            zipcode: address?.zipcode || '',
            country: address?.country || marketplace.basics.address.country,
            main: address?.main
        };
    };

    const hasAddressData = (address: any) => {
        return (
            address &&
            ((address.street && address.street.trim()) ||
                (address.city && address.city.trim()) ||
                (address.state && address.state.trim()) ||
                (address.zipcode && address.zipcode.trim()))
        );
    };

    const getOrderedAddresses = (addresses?: Array<Address>) => {
        const normalized = (address: any, isMain: boolean) => ({
            ...normalizeAddress(address),
            main: isMain
        });

        if (!addresses?.length) {
            return [normalizeAddress({ main: true }), normalizeAddress({ main: false })];
        }

        const mainAddress = addresses.find((a: Address) => a?.main === true) || {};
        const secondaryAddress =
            addresses.find((a: Address) => a?.main === false && a !== mainAddress) ||
            addresses.find((a: Address) => a !== mainAddress) ||
            addresses[1] ||
            {};

        return [normalized(mainAddress, true), normalized(secondaryAddress, false)];
    };

    const getValidAddresses = (addresses?: Array<Address>) => {
        if (!addresses?.length) return [];

        return addresses.filter(address => hasAddressData(address));
    };

    const orderedAddresses = getOrderedAddresses(props.client.addresses);

    const changeValueHandler = (value: any, fieldName?: string) => {
        if (!fieldName) return;

        const updatedClient: any = { ...props.client };

        if (fieldName.startsWith('addresses')) {
            const indexMatch = fieldName.match(/^addresses\[(\d+)\]\.(.+)$/);
            if (indexMatch) {
                const index = Number(indexMatch[1]);
                const addressField = indexMatch[2];

                const existingAddresses = getValidAddresses(updatedClient.addresses);
                const currentOrdered = getOrderedAddresses(updatedClient.addresses);

                let mainAddress = existingAddresses.find(a => a.main === true) || currentOrdered[0];
                let secondaryAddress = existingAddresses.find(a => a.main === false) || currentOrdered[1];

                if (index === 0) {
                    mainAddress = {
                        ...mainAddress,
                        [addressField]: value,
                        main: true,
                        country: mainAddress?.country || marketplace.basics.address.country
                    };
                } else {
                    secondaryAddress = {
                        ...secondaryAddress,
                        [addressField]: value,
                        main: false,
                        country: secondaryAddress?.country || marketplace.basics.address.country
                    };
                }

                const validAddresses = [];
                if (hasAddressData(mainAddress)) {
                    validAddresses.push(mainAddress);
                }
                if (hasAddressData(secondaryAddress)) {
                    validAddresses.push(secondaryAddress);
                }

                updatedClient.addresses = validAddresses;
                props.onChange && props.onChange(updatedClient);
                return;
            }
        }

        set(updatedClient, fieldName, value);
        props.onChange && props.onChange(updatedClient);
    };

    useNonInitialEffect(() => {
        changeValueHandler(props.client.firstName, 'firstName');
        changeValueHandler(props.client.lastName, 'lastName');
        changeValueHandler(props.client.phone, 'phone');
        changeValueHandler(props.client.email, 'email');
        changeValueHandler(props.client.notes, 'notes');
    }, [props.client.id]);

    const spacing = isMobile ? 3 : 4;

    return (
        <Grid
            container
            className={props.className}
            spacing={spacing}
        >
            <Grid
                container
                item
                spacing={spacing}
            >
                <Grid
                    item
                    xs={12}
                    md={6}
                >
                    <ControlledTextField2
                        disabled={props.disabled}
                        labelClassName={classes.inputLabel}
                        placeholder="First Name"
                        label="First Name"
                        name="firstName"
                        id="clients_input_firstname"
                        value={props.client.firstName}
                        onChange={changeValueHandler}
                        autoComplete={'off'}
                        errors={props.errors}
                        required={true}
                        capitalize={true}
                        maxLength={30}
                        allowSpecialCharacters={true}
                        withEmojis={true}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    md={6}
                >
                    <ControlledTextField2
                        disabled={props.disabled}
                        labelClassName={classes.inputLabel}
                        placeholder="Last Name"
                        label="Last Name"
                        name="lastName"
                        id="clients_input_lastname"
                        value={props.client.lastName}
                        autoComplete={'off'}
                        errors={props.errors}
                        onChange={changeValueHandler}
                        required={true}
                        capitalize={true}
                        maxLength={30}
                        allowSpecialCharacters={true}
                        withEmojis={true}
                    />
                </Grid>
            </Grid>
            {(isSensitiveContactEnabled || props.client.id === undefined) && (
                <>
                    <Grid
                        item
                        container
                        spacing={spacing}
                    >
                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <ControlledPhoneField3
                                disabled={props.disabled}
                                labelClassName={classes.inputLabel}
                                label="Phone"
                                placeholder="(___) ___-____"
                                name="phone"
                                autoComplete={'off'}
                                id="clients_input_phone"
                                errors={props.errors}
                                value={props.client.phone}
                                onChange={changeValueHandler}
                                required={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={6}
                        >
                            <ControlledTextField2
                                disabled={props.disabled}
                                labelClassName={classes.inputLabel}
                                label="Email"
                                placeholder="Email"
                                name="email"
                                id="clients_input_email"
                                value={props.client.email}
                                onChange={changeValueHandler}
                                autoComplete={'off'}
                                errors={props.errors}
                                required={true}
                            />
                        </Grid>
                    </Grid>
                    <Grid
                        item
                        xs={12}
                    >
                        <ControlledTextField2
                            disabled={props.disabled}
                            labelClassName={classes.inputLabel}
                            label="Home Address"
                            placeholder="Home Address"
                            name="addresses[0].street"
                            id="clients_input_address_name"
                            value={orderedAddresses[0]?.street || ''}
                            onChange={changeValueHandler}
                            autoComplete={'off'}
                            errors={props.errors}
                        />
                    </Grid>
                    <Grid
                        item
                        container
                        spacing={spacing}
                        alignItems="flex-end"
                    >
                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <ControlledTextField2
                                disabled={props.disabled}
                                labelClassName={classes.inputLabel}
                                label="City"
                                placeholder="City"
                                name="addresses[0].city"
                                id="clients_input_address_city"
                                value={orderedAddresses[0]?.city || ''}
                                onChange={changeValueHandler}
                                autoComplete={'off'}
                                errors={props.errors}
                                capitalize={true}
                                allowSpecialCharacters={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <ControlledTextField2
                                disabled={props.disabled}
                                labelClassName={classes.inputLabel}
                                label="State"
                                placeholder="State"
                                name="addresses[0].state"
                                id="clients_input_address_state"
                                value={orderedAddresses[0]?.state || ''}
                                onChange={changeValueHandler}
                                autoComplete={'off'}
                                errors={props.errors}
                                capitalize={true}
                                allowSpecialCharacters={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <ControlledZipCodeField3
                                disabled={props.disabled}
                                labelClassName={classes.inputLabel}
                                label="ZIP Code"
                                placeholder="ZIP Code"
                                name="addresses[0].zipcode"
                                id="clients_input_address_zipcode"
                                value={orderedAddresses[0]?.zipcode || ''}
                                onChange={changeValueHandler}
                                autoComplete={'off'}
                                errors={props.errors}
                            />
                        </Grid>
                    </Grid>
                    <hr className={classes.lineDivider} />
                    <Grid
                        item
                        xs={12}
                    >
                        <ControlledTextField2
                            disabled={props.disabled}
                            labelClassName={classes.inputLabel}
                            label="Address"
                            placeholder="Address"
                            name="addresses[1].street"
                            id="clients_input_address2_name"
                            value={orderedAddresses[1]?.street || ''}
                            onChange={changeValueHandler}
                            autoComplete={'off'}
                            errors={props.errors}
                        />
                    </Grid>
                    <Grid
                        item
                        container
                        spacing={spacing}
                        alignItems="flex-end"
                    >
                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <ControlledTextField2
                                disabled={props.disabled}
                                labelClassName={classes.inputLabel}
                                label="City"
                                placeholder="City"
                                name="addresses[1].city"
                                id="clients_input_address2_city"
                                value={orderedAddresses[1]?.city || ''}
                                onChange={changeValueHandler}
                                autoComplete={'off'}
                                errors={props.errors}
                                capitalize={true}
                                allowSpecialCharacters={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <ControlledTextField2
                                disabled={props.disabled}
                                labelClassName={classes.inputLabel}
                                label="State"
                                placeholder="State"
                                name="addresses[1].state"
                                id="clients_input_address2_state"
                                value={orderedAddresses[1]?.state || ''}
                                onChange={changeValueHandler}
                                autoComplete={'off'}
                                errors={props.errors}
                                capitalize={true}
                                allowSpecialCharacters={true}
                            />
                        </Grid>
                        <Grid
                            item
                            xs={12}
                            md={4}
                        >
                            <ControlledZipCodeField3
                                disabled={props.disabled}
                                labelClassName={classes.inputLabel}
                                label="ZIP Code"
                                placeholder="ZIP Code"
                                name="addresses[1].zipcode"
                                id="clients_input_address2_zipcode"
                                value={orderedAddresses[1]?.zipcode || ''}
                                onChange={changeValueHandler}
                                autoComplete={'off'}
                                errors={props.errors}
                            />
                        </Grid>
                    </Grid>
                </>
            )}
            <Grid
                item
                xs={12}
            >
                <ControlledDescriptionField2
                    disabled={props.disabled}
                    labelClassName={classes.inputLabel}
                    label="Client Notes"
                    id="clients_input_notes"
                    maxRows={3}
                    minRows={3}
                    placeholder="Anything important to note?"
                    name="notes"
                    value={props.client.notes}
                    onChange={changeValueHandler}
                    errors={props.errors}
                />
            </Grid>
        </Grid>
    );
};

export default PersonalInformation;
